import { Action, AsyncThunkAction, configureStore, ThunkAction } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import * as Sentry from '@sentry/react';
import { reportSagaError } from 'utils/sentry/reportSagaError';
import { redactStateIceServers } from 'utils/sentry/redactIceServers';
import { NOISY_ACTION_TYPES, toReduxBreadcrumb } from 'utils/sentry/breadcrumbNoise';
import { Dispatch } from 'redux';
import { listenerMiddleware } from 'store/listenerMiddleware';
// import { generateUser, generateUsers, mockUsersState } from 'utils/tests/mock-data/users';
// import { mockPermissions } from 'utils/tests/mock-data/permissions';
import rootReducer from './rootReducer';
import rootSaga from './rootSaga';

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer: toReduxBreadcrumb,
  stateTransformer: redactStateIceServers,
});

// @FIXME fix type
export function initStore(preloadedState?: any) {
  const sagaMiddleware = createSagaMiddleware({ onError: reportSagaError });
  const store = configureStore({
    preloadedState,
    reducer: rootReducer,
    devTools: {
      actionsDenylist: [...NOISY_ACTION_TYPES],
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [
            'streaming/remoteTrackReceived',
            'contentLibrary/fileUploadRequested',
            'quizzes/importQuizCsvRequested',
            'quizzes/importQuizTemplateCsvRequested',
            'polls/importPollCsvRequested',
            'polls/importPollTemplateCsvRequested',
            'virtualBackground/customVirtualBackgroundUploadRequested',
            'chat/chatImagesAdded',
          ],
          ignoredPaths: ['chat.pendingImages'],
        },
      })
        .prepend(listenerMiddleware.middleware)
        .concat([
          sagaMiddleware,
          // ...(webrtcAdapter.browserDetails.browser === 'safari' ? [logger] : []),
        ]),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
  });

  sagaMiddleware.run(rootSaga);
  return store;
}

// const preloadedState = {
//   permissions: mockPermissions(),
//   users: mockUsersState({
//     local: generateUser(),
//     users: generateUsers(2000),
//     waitingEntry: generateUsers(30),
//   }),
// };

export const store = initStore();
export const { getState, dispatch } = store;

export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;
export type RootState = ReturnType<typeof rootReducer>;

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

// re-export of private type from redux-toolkit
type AsyncThunkConfig = {
  state?: unknown;
  dispatch?: Dispatch;
  extra?: unknown;
  rejectValue?: unknown;
  serializedErrorType?: unknown;
  pendingMeta?: unknown;
  fulfilledMeta?: unknown;
  rejectedMeta?: unknown;
};

export type SagaWrappedThunkAction<
  Returned,
  ThunkArg,
  ThunkApiConfig extends AsyncThunkConfig,
> = ReturnType<AsyncThunkAction<Returned, ThunkArg, ThunkApiConfig>>;
